import { useEffect, useMemo, useState } from "react";

import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Stack,
  Chip,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";

import { API_URL } from "../config/api";
import { getAmountDue } from "../utils/categories";
import type { Judoka } from "../models/Judoka";
import { adminFetch } from "../services/adminAuth";

type JudokaRow = Judoka & { id: number };

type Props = {
  // Si renseigné : seulement les judokas de ce code d'inscription.
  registrationCode?: string;
};

const rowKey = (j: JudokaRow) => `${j.registrationCode}:${j.id}`;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("fr-BE");

function LicenceStatusChip({ judoka }: { judoka: JudokaRow }) {
  const check = judoka.licenceCheck;

  if (judoka.licenceStatus === "valid" && check) {
    return (
      <Tooltip
        title={`Valable jusqu'au ${formatDate(check.expiry)} (${
          check.via === "listing"
            ? "vérifiée dans le listing de la fédération"
            : "vérifiée via le QR code"
        })`}
      >
        <Chip label="V" color="success" size="small" />
      </Tooltip>
    );
  }

  if (judoka.licenceStatus === "invalid" && check) {
    return (
      <Tooltip
        title={
          check.suspended
            ? "Licence suspendue"
            : `Expire le ${formatDate(check.expiry)}, avant la date du tournoi`
        }
      >
        <Chip label="X" color="error" size="small" />
      </Tooltip>
    );
  }

  const reason =
    judoka.source === "qr"
      ? "Vérification impossible (n° de licence modifié après le scan, ou JudoWB injoignable)"
      : judoka.source === "import"
        ? "Inscrit par import Excel, licence introuvable dans le listing de la fédération"
        : "Licence introuvable dans le listing de la fédération (judoka étranger, n° ou prénom différent…) : pas d'information sur la licence";

  return (
    <Tooltip title={reason}>
      <Chip label="?" size="small" />
    </Tooltip>
  );
}

export default function RegistrationTracking({ registrationCode }: Props) {
  const [judokas, setJudokas] = useState<JudokaRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [loadError, setLoadError] = useState("");

  const loadJudokas = async () => {
    const response = await adminFetch(`${API_URL}/judokas`);

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      setLoadError(body.error ?? `Erreur ${response.status}`);
      setLoading(false);
      return;
    }

    const data: JudokaRow[] = await response.json();

    setJudokas(
      registrationCode
        ? data.filter((j) => j.registrationCode === registrationCode)
        : data
    );
    setLoading(false);
  };

  useEffect(() => {
    loadJudokas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationCode]);

  const sorted = useMemo(
    () =>
      [...judokas].sort((a, b) =>
        `${a.clubName}${a.lastName}`.localeCompare(
          `${b.clubName}${b.lastName}`
        )
      ),
    [judokas]
  );

  const amountDueOf = (j: JudokaRow) =>
    j.amountDue ?? getAmountDue(j.category);

  const totalDue = judokas.reduce(
    (sum, j) => sum + amountDueOf(j),
    0
  );

  const totalPaid = judokas
    .filter((j) => j.paid)
    .reduce((sum, j) => sum + amountDueOf(j), 0);

  const toggleRow = (j: JudokaRow) => {
    const key = rowKey(j);
    const next = new Set(selected);

    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }

    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === sorted.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(sorted.map(rowKey)));
    }
  };

  const markSelected = async (paid: boolean) => {
    const items = sorted
      .filter((j) => selected.has(rowKey(j)))
      .map((j) => ({
        registrationCode: j.registrationCode,
        id: j.id,
      }));

    if (items.length === 0) {
      return;
    }

    await adminFetch(`${API_URL}/judokas/mark-paid`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items, paid }),
    });

    setMessage(
      paid
        ? `${items.length} judoka(s) marqué(s) en ordre.`
        : `${items.length} judoka(s) repassé(s) en attente.`
    );

    setSelected(new Set());
    await loadJudokas();
  };

  if (loading) {
    return <Typography>Chargement...</Typography>;
  }

  if (loadError) {
    return <Alert severity="error">{loadError}</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" spacing={3} useFlexGap flexWrap="wrap" sx={{ mb: 3 }}>
        <Typography>
          {judokas.length} judoka(s)
        </Typography>
        <Typography>
          Total dû : <strong>{totalDue} €</strong>
        </Typography>
        <Typography>
          Encaissé : <strong>{totalPaid} €</strong>
        </Typography>
        <Typography>
          Restant : <strong>{totalDue - totalPaid} €</strong>
        </Typography>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="success"
          disabled={selected.size === 0}
          onClick={() => markSelected(true)}
        >
          Mettre en ordre ({selected.size})
        </Button>

        <Button
          variant="outlined"
          disabled={selected.size === 0}
          onClick={() => markSelected(false)}
        >
          Repasser en attente ({selected.size})
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={
                    sorted.length > 0 &&
                    selected.size === sorted.length
                  }
                  indeterminate={
                    selected.size > 0 &&
                    selected.size < sorted.length
                  }
                  onChange={toggleAll}
                />
              </TableCell>
              <TableCell>Club</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Prénom</TableCell>
              <TableCell>Licence</TableCell>
              <TableCell>Licence valide</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Nationalité</TableCell>
              <TableCell>Naissance</TableCell>
              <TableCell>Catégorie</TableCell>
              <TableCell>Montant</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Inscrit le</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sorted.map((j) => (
              <TableRow
                key={rowKey(j)}
                selected={selected.has(rowKey(j))}
                hover
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.has(rowKey(j))}
                    onChange={() => toggleRow(j)}
                  />
                </TableCell>
                <TableCell>{j.clubName}</TableCell>
                <TableCell>{j.lastName}</TableCell>
                <TableCell>{j.firstName}</TableCell>
                <TableCell>{j.licence}</TableCell>
                <TableCell>
                  <LicenceStatusChip judoka={j} />
                </TableCell>
                <TableCell>{j.email}</TableCell>
                <TableCell>{j.nationality}</TableCell>
                <TableCell>{j.birthDate}</TableCell>
                <TableCell>{j.category}</TableCell>
                <TableCell>
                  {amountDueOf(j) > 0
                    ? `${amountDueOf(j)} €`
                    : "Gratuit"}
                </TableCell>
                <TableCell>
                  {j.paid ? (
                    <Chip
                      label={j.paidVia === "mollie" ? "Payé en ligne" : "En ordre"}
                      color="success"
                      size="small"
                    />
                  ) : (
                    <Chip
                      label="En attente"
                      color="warning"
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  {j.createdAt ? formatDate(j.createdAt) : ""}
                </TableCell>
              </TableRow>
            ))}

            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  Aucune inscription pour le moment.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={!!message}
        autoHideDuration={5000}
        onClose={() => setMessage("")}
      >
        <Alert
          onClose={() => setMessage("")}
          severity="success"
          variant="filled"
        >
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
